import React, { useEffect, useState } from "react";
import { Switch } from "@/components/ui/switch";
import api from "@/lib/api";
import { Loader2 } from "lucide-react";

export const AffiliateIntegrations = () => {
  const [status, setStatus] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const load = () => api.get("/admin/affiliate/status").then((r) => setStatus(r.data))
    .catch(() => setError("Could not load affiliate status."));
  useEffect(() => { load(); }, []);

  const save = async (patch) => {
    setSaving(true); setError("");
    try {
      const r = await api.put("/admin/affiliate/settings", {
        sovrn_enabled: status.sovrn.enabled, sovrn_automation: status.sovrn.automation, ...patch,
      });
      setStatus(r.data);
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not save that change.");
    } finally { setSaving(false); }
  };

  if (!status) return <Loader2 className="w-5 h-5 animate-spin" data-testid="affiliate-loading" />;
  const s = status.sovrn;
  const c = status.coverage;

  return (
    <div className="space-y-6" data-testid="affiliate-panel">
      <div className="bg-white rounded-2xl border border-brand-cream2 p-6">
        <h3 className="font-serif text-xl mb-1">Sovrn Commerce</h3>
        <p className="text-sm text-muted-foreground mb-5">
          Covers retailers Awin doesn't reach. Paste links by hand until the campaign is approved,
          then switch automation on.
        </p>
        <p className="text-sm mb-4" data-testid="sovrn-credentials">
          {s.credentials_configured
            ? `Credentials configured${s.key_hint ? ` (API key ${s.key_hint})` : ""}.`
            : s.api_key_present || s.secret_key_present
              ? "Partly configured — both the API key and the secret key are needed."
              : "No credentials yet. Add SOVRN_API_KEY and SOVRN_SECRET_KEY to server configuration."}
        </p>
        <Row label="Sovrn integration" help="Use pasted Sovrn links on product CTAs."
          checked={s.enabled} disabled={saving} testid="sovrn-enabled-toggle"
          onChange={(v) => save({ sovrn_enabled: v, sovrn_automation: v ? s.automation : false })} />
        <Row label="Automated link generation"
          help="Generate a Sovrn link for products without one. Needs credentials and campaign approval."
          checked={s.automation} disabled={saving || !s.enabled || !s.api_key_present}
          testid="sovrn-automation-toggle" onChange={(v) => save({ sovrn_automation: v })} />
        {error && <p className="mt-4 text-sm text-brand-burgundy" data-testid="affiliate-error">{error}</p>}
      </div>

      <div className="bg-white rounded-2xl border border-brand-cream2 p-6">
        <h3 className="font-serif text-xl mb-1">Awin</h3>
        <p className="text-sm text-muted-foreground">
          {status.awin.configured ? "Connected." : "Not configured."} Deep links come straight from the
          retailer feeds and always take priority over Sovrn. Managed in the Retailer Feed tab.
        </p>
      </div>

      <div className="bg-white rounded-2xl border border-brand-cream2 p-6">
        <h3 className="font-serif text-xl mb-4">Affiliate coverage</h3>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4" data-testid="affiliate-coverage">
          <Count label="Awin links" value={c.awin} testid="coverage-awin" />
          <Count label="Sovrn links" value={c.sovrn} testid="coverage-sovrn" />
          <Count label="Direct links" value={c.direct} testid="coverage-direct" />
          <Count label="No affiliate link" value={c.none} testid="coverage-none" />
        </div>
      </div>
    </div>
  );
};

const Row = ({ label, help, checked, disabled, onChange, testid }) => (
  <div className="flex items-start justify-between gap-6 py-3 border-t border-brand-cream2 first:border-t-0">
    <div>
      <p className="text-sm font-medium">{label}</p>
      <p className="text-xs text-muted-foreground mt-0.5 max-w-md">{help}</p>
    </div>
    <Switch checked={checked} disabled={disabled} onCheckedChange={onChange} data-testid={testid} />
  </div>
);

const Count = ({ label, value, testid }) => (
  <div className="rounded-xl bg-brand-cream2/50 p-4">
    <p className="font-mono text-2xl text-brand-green" data-testid={testid}>{value}</p>
    <p className="text-xs text-muted-foreground mt-1">{label}</p>
  </div>
);
